import Locations from './Locations'
import {
  Schedule,
  EventNote,
  AirlineSeatReclineNormal,
  Chat,
  Groups,
  Person
} from '@mui/icons-material'
import { faVanShuttle, faTaxi, faUserClock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import formatPrice from '../../utils/formatPrice'

const Card = ({ lobby, setSelected, userId }) => {
  const occupiedSeats = lobby.companions.reduce((acc, companion) => acc + companion.seats, 0)
  const remainingSeats = lobby.seats_available - occupiedSeats

  // Estado del lobby
  const today = new Date(new Date().getTime() - 3 * 60 * 60 * 1000).toISOString()
  const full = remainingSeats === 0
  const completed = Boolean(lobby.completed)
  const inProgress = today >= lobby.Journey.departure_time && today <= lobby.Journey.estimated_time
  const finished = today >= lobby.Journey.estimated_time
  const cancelled = lobby.cancelled

  // Formatear fecha y hora
  const departureTime = lobby.Journey.departure_time.substr(
    lobby.Journey.departure_time.indexOf('T') + 1,
    5
  )
  const formattedDate =
    lobby.Journey.departure_time.substr(lobby.Journey.departure_time.indexOf('-') + 4, 2) +
    '/' +
    lobby.Journey.departure_time.substr(lobby.Journey.departure_time.indexOf('-') + 1, 2) +
    (lobby.Journey.departure_time.substr(0, 4) !== today.substr(0, 4)
      ? '/' + lobby.Journey.departure_time.substr(0, 4)
      : '')

  // Determinar el estado principal
  const getStatus = () => {
    if (cancelled)
      return {
        text: 'Cancelado',
        icon: '✕',
        badgeClass: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400',
        borderClass: 'border-l-red-600'
      }
    if (completed)
      return {
        text: 'Concretado',
        icon: '✓',
        badgeClass: 'bg-green-50 dark:bg-emerald-500/10 text-green-600 dark:text-emerald-400',
        borderClass: 'border-l-green-600'
      }
    if (finished)
      return {
        text: 'Terminado',
        icon: '◉',
        badgeClass: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400',
        borderClass: 'border-l-blue-600'
      }
    if (inProgress)
      return {
        text: 'En curso',
        icon: '▶',
        badgeClass: 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400',
        borderClass: 'border-l-violet-600'
      }
    if (full)
      return {
        text: 'Lleno',
        icon: '●',
        badgeClass: 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400',
        borderClass: 'border-l-orange-500'
      }
    return {
      text: 'Disponible',
      icon: '○',
      badgeClass: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      borderClass: 'border-l-emerald-500'
    }
  }

  const status = getStatus()
  const hasPendingForUser = lobby.pending_companions?.find(
    companion => companion.user_id === userId
  )

  // Clases para los chips de asientos
  const getSeatsChipClass = () => {
    if (remainingSeats === 0) return 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/30'
    if (remainingSeats <= 2) return 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
    return 'bg-slate-50 dark:bg-dk-raised text-slate-600 dark:text-gray-300 border-slate-200 dark:border-dk-border'
  }

  const getSeatsIconClass = () => {
    if (remainingSeats === 0) return 'text-red-500 dark:text-red-400'
    if (remainingSeats <= 2) return 'text-amber-500 dark:text-amber-400'
    return 'text-slate-400 dark:text-gray-400'
  }

  return (
    <div
      onClick={() => setSelected(lobby)}
      className={`
        flex flex-col gap-3.5 p-4 mx-4 mb-3.5 rounded-xl cursor-pointer
        shadow-[0_1px_3px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.05)]
        border-l-4 border bg-white dark:bg-dk-surface
        ${hasPendingForUser ? 'border-orange-500' : ''}
        ${lobby.type === 'van' || lobby.type === 'taxi' ? 'border-[#FDB813]' : 'border-gray dark:border-dk-border'}
        ${status.borderClass}
      `}
    >
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-2'>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold ${status.badgeClass}`}
          >
            <span className='text-[10px]'>{status.icon}</span>
            {status.text}
          </span>
          {(lobby.type === 'van' || lobby.type === 'taxi') && (
            <FontAwesomeIcon
              icon={lobby.type === 'van' ? faVanShuttle : faTaxi}
              className='text-lg'
              style={{ color: '#FDB813' }}
            />
          )}
        </div>
        <div className='flex items-center gap-3'>
          {!cancelled && lobby.chats > 0 && (
            <div className='flex items-center gap-1 bg-blue-500 text-white px-2 py-1 rounded-md text-xs font-semibold'>
              <Chat className='!text-sm' />
              <span>{lobby.chats}</span>
            </div>
          )}
          <span className='text-base font-bold text-gray-800 dark:text-gray-100'>
            (${formatPrice(lobby.price_without_fee)})
          </span>
          <span className='text-lg font-bold text-gray-800 dark:text-gray-100'>${formatPrice(lobby.price)}</span>
        </div>
      </div>

      {/* Ubicaciones */}
      <Locations
        originName={lobby.Journey.origin_name.locality}
        destinationName={lobby.Journey.destination_name.locality}
      />

      {/* Info Row - Fecha, hora y asientos */}
      <div className='flex items-center gap-2 flex-wrap'>
        <div className='inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium bg-slate-50 dark:bg-dk-raised text-slate-600 dark:text-gray-300 border border-slate-200 dark:border-dk-border'>
          <EventNote className='!text-base text-slate-400 dark:text-gray-400' />
          <span>{formattedDate}</span>
        </div>
        <div className='inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium bg-slate-50 dark:bg-dk-raised text-slate-600 dark:text-gray-300 border border-slate-200 dark:border-dk-border'>
          <Schedule className='!text-base text-slate-400 dark:text-gray-400' />
          <span>{departureTime}</span>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium border ${getSeatsChipClass()}`}
        >
          <AirlineSeatReclineNormal className={`!text-base ${getSeatsIconClass()}`} />
          <span>{remainingSeats}</span>
        </div>
      </div>

      {/* Footer */}
      <div className='flex justify-between items-center pt-3 border-t border-dashed border-gray-200 dark:border-dk-border'>
        <div className='flex gap-2'>
          {lobby.companions.length > 0 && (
            <div className='inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 dark:bg-blue-500/10 text-blue-800 dark:text-blue-400 text-xs font-semibold'>
              <Groups className='!text-sm text-blue-500' />
              <span>{occupiedSeats}</span>
            </div>
          )}
          {lobby.pending_companions?.length > 0 && (
            <div className='inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 dark:bg-amber-500/10 text-amber-800 dark:text-amber-400 text-xs font-semibold'>
              <FontAwesomeIcon icon={faUserClock} className='text-sm text-amber-500' />
              <span>{lobby.pending_companions.length}</span>
            </div>
          )}
        </div>
        <div className='flex items-center gap-1.5 text-base text-gray-500 dark:text-gray-400 font-medium'>
          <Person className='text-base text-gray-400 dark:text-gray-400' />
          <span>{lobby.Owner_info.first_name}</span>
        </div>
      </div>
    </div>
  )
}

export default Card
